import { useEffect, useMemo } from 'react';
import { incidentsApi, type Incident, type IncidentGroup } from '../../services/incidentsApi';
import { incidentDisplayTitleForRow } from './incidentDisplayModel';
import { formatWorkflowStateLabel } from '../../utils/incidentStateLabels';
import { getGroupedReviewRecurrenceStatusLabel, getIncidentGroupPatternLabel } from '../../domain/incidents/incidentPatternSemantics';
import {
  getIncidentPatternReadVolumeCount,
  getIncidentPatternWindowSignalCount,
  resolvePatternVolumeKind,
} from '../../domain/incidents/patternReviewScopeSemantics';
import { filterIncidentsToPatternScope } from '../../domain/incidents/patternDispositionSummary';
import { resolveIncidentWorkflowProblemContext } from '../../domain/safetyCases/workflowGlueNavigation';
import {
  resolveIncidentLocationPath,
  sanitizeCustomerLocationPath,
} from '../../utils/locationDisplay';

type UseIncidentsReviewPageArgs = {
  reviewMode: boolean;
  reviewExactMode: boolean;
  heatmapOpening: boolean;
  searchParams: URLSearchParams;
  heatmapPinnedIncidentId: string | null;
  setHeatmapPinnedIncidentId: (value: string | null) => void;
  selectedIncident: Incident | null;
  selectedGroupContext: IncidentGroup | null;
  setSelectedIncident: (value: Incident | null) => void;
  incidents: Incident[];
  handleOpenIncidentReviewPage: (incidentId: string, options?: { exact?: boolean }) => void;
};

export function useIncidentsReviewPage({
  reviewMode,
  reviewExactMode: _reviewExactMode,
  heatmapOpening: _heatmapOpening,
  searchParams,
  heatmapPinnedIncidentId,
  setHeatmapPinnedIncidentId,
  selectedIncident,
  selectedGroupContext,
  setSelectedIncident,
  incidents,
  handleOpenIncidentReviewPage: _handleOpenIncidentReviewPage,
}: UseIncidentsReviewPageArgs) {
  const reviewPatternLabel = useMemo(() => {
    const groupedLabel = getIncidentGroupPatternLabel(selectedGroupContext);
    if (groupedLabel) return groupedLabel;
    const source: any = selectedIncident;
    return String(
      source?.pattern_source?.pattern_label
      || source?.risk_pattern?.label
      || source?.recurrence_tag
      || '',
    ).trim();
  }, [selectedGroupContext, selectedIncident]);
  const reviewWorkspaceTitle = reviewPatternLabel || (selectedIncident
    ? incidentDisplayTitleForRow(selectedIncident as any, undefined, {
      zoneName: String((selectedIncident as any)?.zone_id || '').trim(),
    })
    : selectedGroupContext
      ? incidentDisplayTitleForRow(selectedGroupContext as any, undefined, {
        zoneName: String((selectedGroupContext as any)?.zone_id || '').trim(),
      })
      : '');
  const reviewWorkspaceId = String(
    selectedIncident?.incident_id ||
      (selectedGroupContext as any)?.linked_detailed_record_id ||
      (selectedGroupContext as any)?.incident_id ||
      '',
  ).trim();
  const reviewPatternVolumeCount = Math.max(
    0,
    getIncidentPatternReadVolumeCount(selectedGroupContext as any),
    getIncidentPatternReadVolumeCount(selectedIncident as any),
  );
  const observationParentCaseCount = Math.max(
    0,
    getIncidentPatternWindowSignalCount(selectedGroupContext as any),
    getIncidentPatternWindowSignalCount(selectedIncident as any),
    Number((selectedGroupContext as any)?.alert_count || 0),
  );
  const reviewPatternVolumeKind = resolvePatternVolumeKind(
    (selectedGroupContext as any) || selectedIncident,
  );
  const reviewRelatedIncidentCount = reviewPatternVolumeCount;
  const reviewSeriesActionLabel =
    reviewRelatedIncidentCount > 1
      ? 'Drill into observations'
      : '';
  const reviewObservationLocationLine = useMemo(() => {
    const source: any = selectedIncident || selectedGroupContext;
    // Shared Location rule: sanitize snapshot path; never Demo* / raw id chrome.
    return sanitizeCustomerLocationPath(resolveIncidentLocationPath(source));
  }, [selectedGroupContext, selectedIncident]);

  const patternScopeIncidents = useMemo(() => {
    const source: any = selectedGroupContext || selectedIncident;
    if (!source) return [] as Incident[];
    const scoped = filterIncidentsToPatternScope(
      (incidents || []) as unknown as Array<Record<string, unknown>>,
      source as Record<string, unknown>,
    ) as unknown as Incident[];
    const byId = new Map<string, Incident>();
    for (const candidate of scoped) {
      if (!candidate?.incident_id) continue;
      byId.set(String(candidate.incident_id), candidate);
    }
    if (selectedIncident?.incident_id) {
      const inScope = filterIncidentsToPatternScope(
        [selectedIncident as unknown as Record<string, unknown>],
        source as Record<string, unknown>,
      ).length > 0;
      if (inScope) byId.set(String(selectedIncident.incident_id), selectedIncident);
    }
    return Array.from(byId.values());
  }, [incidents, selectedGroupContext, selectedIncident]);

  const reviewSeriesIncidents = useMemo(() => {
    return [...patternScopeIncidents]
      .sort((a, b) => {
        const aTs =
          new Date(String(a.last_edge_update_at || a.updated_at || a.created_at || a.ts_start || '')).getTime() || 0;
        const bTs =
          new Date(String(b.last_edge_update_at || b.updated_at || b.created_at || b.ts_start || '')).getTime() || 0;
        return bTs - aTs;
      })
      .slice(0, 6);
  }, [patternScopeIncidents]);

  const reviewSeriesHiddenCount = Math.max(0, reviewRelatedIncidentCount - reviewSeriesIncidents.length);
  const reviewGroupedWorkflowLabel = useMemo(() => {
    if (!selectedIncident) return 'Needs review';
    return String(formatWorkflowStateLabel(selectedIncident) || 'Needs review');
  }, [selectedIncident]);
  const reviewGroupedRecurrenceLabel = useMemo(
    () => getGroupedReviewRecurrenceStatusLabel((selectedGroupContext as any) || selectedIncident),
    [selectedGroupContext, selectedIncident],
  );
  const workflowProblemContext = useMemo(() => {
    const source: any = selectedIncident || selectedGroupContext;
    return resolveIncidentWorkflowProblemContext({
      workflowId: searchParams.get('workflow_id') || source?.workflow_id,
      groupKey: searchParams.get('group_key') || source?.group_key,
      conditionId: searchParams.get('condition_id'),
      incidentType: searchParams.get('incident_type') || source?.incident_type,
      zoneId: searchParams.get('zone_id') || source?.zone_id,
      cameraId: searchParams.get('camera_id') || source?.camera_id,
    });
  }, [searchParams, selectedGroupContext, selectedIncident]);
  useEffect(() => {
    const pinnedId = String(heatmapPinnedIncidentId || '').trim();
    if (!reviewMode || !pinnedId) return;
    if (String(selectedIncident?.incident_id || '').trim() === pinnedId) return;
    let cancelled = false;
    void (async () => {
      try {
        const fresh = await incidentsApi.getIncident(pinnedId);
        if (!cancelled) setSelectedIncident(fresh);
      } catch {
        // keep last good selection if refetch fails
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [heatmapPinnedIncidentId, reviewMode, selectedIncident?.incident_id, setSelectedIncident]);

  useEffect(() => {
    if (!String(searchParams.get('heatmap_hour') || '').trim()) {
      setHeatmapPinnedIncidentId(null);
    }
  }, [searchParams, setHeatmapPinnedIncidentId]);

  // Do not auto-jump to the newest table row here — that often lacks cloud video and
  // overrides the cloud-proof representative pick in useIncidentsPageOrchestration.

  return {
    reviewWorkspaceTitle,
    reviewPatternLabel,
    reviewObservationLocationLine,
    reviewWorkspaceId,
    reviewRelatedIncidentCount,
    reviewPatternVolumeCount,
    observationParentCaseCount,
    reviewPatternVolumeKind,
    reviewSeriesActionLabel,
    reviewSeriesIncidents,
    patternScopeIncidents,
    reviewSeriesHiddenCount,
    reviewGroupedWorkflowLabel,
    reviewGroupedRecurrenceLabel,
    workflowProblemContext,
  };
}
